import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, ArrowLeft, Star, Check, Truck, ShieldCheck, Minus, Plus } from 'lucide-react';
import Seo from '../components/Seo';
import useReveal from '../hooks/useReveal';
import { useLang } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { SHOP_PRODUCTS } from '../mock/shop';
import { tProduct, tComingSoon } from '../i18n/shopI18n';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const { add } = useCart();
  const product = SHOP_PRODUCTS.find((p) => p.id === id);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  useReveal(lang + id);
  useEffect(() => { window.scrollTo(0, 0); setQty(1); }, [id]);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-serif text-3xl text-espresso">Product not found</p>
        <Link to="/shop" className="text-gold underline">Back to store</Link>
      </div>
    );
  }

  const t = tProduct(product, lang);
  const related = SHOP_PRODUCTS.filter((p) => p.brand === product.brand && p.id !== product.id).slice(0, 4);
  const addToBag = () => { add(product, qty); setAdded(true); setTimeout(() => setAdded(false), 1200); };
  const buyNow = () => { add(product, qty); navigate('/checkout'); };

  const productJsonLd = {
    '@context': 'https://schema.org', '@type': 'Product',
    name: product.name, brand: { '@type': 'Brand', name: product.brand },
    image: product.image, description: product.desc,
    offers: {
      '@type': 'Offer', priceCurrency: 'GBP', price: product.comingSoon ? '0.00' : product.price.toFixed(2),
      availability: product.comingSoon ? 'https://schema.org/PreOrder' : 'https://schema.org/InStock',
      url: `https://argecosmetics.co.uk/shop/${product.id}`,
    },
  };

  return (
    <div className="pt-28">
      <Seo title={`${t.name} — ${product.brand}`} path={`/shop/${product.id}`} type="product"
        description={t.desc?.slice(0, 155)} image={product.image} jsonLd={productJsonLd} />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-mocha hover:text-gold transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> Back to store</Link>
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Big image */}
          <div className="reveal bg-white border border-gold-soft p-10 flex items-center justify-center min-h-[420px]">
            <img src={product.image} alt={t.name} className="max-h-[460px] w-auto object-contain" />
          </div>
          {/* Info */}
          <div className="reveal">
            <p className="text-[0.7rem] tracking-[0.25em] uppercase text-gold">{product.brand}</p>
            <h1 className="font-serif text-4xl md:text-5xl text-espresso mt-2 leading-tight">{t.name}</h1>
            <div className="flex items-center gap-1 mt-3 text-gold">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-gold" />)}<span className="text-xs text-mocha ml-2">{product.comingSoon ? tComingSoon(lang) : 'In stock'}</span></div>
            <div className="mt-5 flex items-baseline gap-3">
              {product.comingSoon ? (
                <span className="font-serif text-3xl text-gold italic" data-testid="pd-coming-soon">{tComingSoon(lang)}</span>
              ) : (
                <>
                  <span className="font-serif text-4xl text-espresso">£{product.price.toFixed(2)}</span>
                  {product.oldPrice ? <span className="text-lg text-mocha line-through">£{product.oldPrice.toFixed(2)}</span> : null}
                  <span className="text-xs text-mocha">VAT included</span>
                </>
              )}
            </div>
            <p className="mt-6 text-mocha leading-relaxed">{t.desc}</p>

            {product.comingSoon && (
              <Link to="/dopamine" data-testid="pd-waitlist-link"
                className="mt-8 gold-sheen inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-espresso text-ivory text-xs tracking-[0.16em] uppercase hover:bg-gold transition-colors">
                {tComingSoon(lang)} — Join the Waitlist
              </Link>
            )}

            {!product.comingSoon && (
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center border border-gold-soft">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 text-espresso hover:text-gold"><Minus className="w-4 h-4" /></button>
                <span className="px-5 text-espresso">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3 text-espresso hover:text-gold"><Plus className="w-4 h-4" /></button>
              </div>
              <button onClick={addToBag} className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-espresso text-espresso text-xs tracking-[0.16em] uppercase hover:bg-espresso hover:text-ivory transition-colors">
                {added ? <><Check className="w-4 h-4" /> Added</> : <><ShoppingBag className="w-4 h-4" /> Add to Bag</>}
              </button>
              <button onClick={buyNow} className="flex-1 min-w-[160px] gold-sheen inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-espresso text-ivory text-xs tracking-[0.16em] uppercase hover:bg-gold transition-colors">Buy Now</button>
            </div>
            )}

            <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm text-mocha">
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-gold" /> Free UK delivery over £40</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" /> Authentic ARGE brand</span>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="py-20">
            <h2 className="font-serif text-3xl text-espresso mb-8">More from {product.brand}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => {
                const rt = tProduct(p, lang);
                return (
                <Link to={`/shop/${p.id}`} key={p.id} className="group bg-ivory border border-transparent hover:border-gold-soft hover-lift">
                  <div className="bg-white p-6 aspect-square"><img src={p.image} alt={rt.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-4"><p className="text-[0.6rem] tracking-[0.15em] uppercase text-gold">{p.brand}</p><p className="text-sm text-espresso mt-1 line-clamp-2 min-h-[2.5rem]">{rt.name}</p><p className="font-serif text-lg text-espresso mt-2">{p.comingSoon ? tComingSoon(lang) : `£${p.price.toFixed(2)}`}</p></div>
                </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
